import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  DatePicker,
  message,
  Space,
  Typography,
  Row,
  Col,
  Tag,
  Spin,
  Modal,
  Statistic,
  Select,
} from 'antd';
import {
  FileExcelOutlined,
  DownloadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import { ref, get, set, push } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import * as XLSX from 'xlsx';

dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Raporty = () => {
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().startOf('isoWeek'), dayjs().endOf('isoWeek')]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('gotowka');
  const [selectedFormat, setSelectedFormat] = useState('csv');

  // Автоматически меняем формат в зависимости от типа отчёта
  useEffect(() => {
    if (selectedReportType === 'gotowka') {
      setSelectedFormat('csv');
    } else if (selectedReportType === 'umowa_najmu') {
      setSelectedFormat('excel');
    }
  }, [selectedReportType]);

  // Загрузка истории отчётов
  useEffect(() => {
    loadReportsHistory();
  }, [db]);

  const loadReportsHistory = async () => {
    try {
      const reportsRef = ref(db, 'reports');
      const snapshot = await get(reportsRef);
      if (snapshot.exists()) {
        const reports = [];
        snapshot.forEach((childSnapshot) => {
          reports.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        // Сортируем по дате создания (новые сверху)
        reports.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setGeneratedReports(reports);
      }
    } catch (error) {
      console.error('Error loading reports history:', error);
    }
  };

  // Получение всех недель для выбранного месяца
  const getWeeksInMonth = (startDate, endDate) => {
    const weeks = [];
    let current = dayjs(startDate).startOf('isoWeek');
    const end = dayjs(endDate).endOf('isoWeek');

    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const weekNumber = current.isoWeek();
      const year = current.year();
      const weekKey = `${weekNumber}-${year}`;

      // Проверяем, что неделя попадает в выбранный диапазон
      const weekStart = current.startOf('isoWeek');
      const weekEnd = current.endOf('isoWeek');

      if (weekEnd.isAfter(startDate) && weekStart.isBefore(endDate)) {
        weeks.push({
          weekKey,
          weekNumber,
          year,
          startDate: weekStart.format('YYYY-MM-DD'),
          endDate: weekEnd.format('YYYY-MM-DD'),
        });
      }

      current = current.add(1, 'week');
    }

    return weeks;
  };

  // Генерация отчёта по umowa_najmu
  const generateUmowaNajmuReport = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error('Wybierz okres dla raportu');
      return;
    }

    setLoading(true);
    const startDate = dateRange[0];
    const endDate = dateRange[1];

    try {
      message.loading('Generowanie raportu...', 0);

      // Получаем все недели в выбранном месяце
      const weeks = getWeeksInMonth(startDate, endDate);

      if (weeks.length === 0) {
        message.destroy();
        message.warning('Brak tygodni w wybranym okresie');
        setLoading(false);
        return;
      }

      // Загружаем всех водителей
      const driversSnapshot = await get(ref(db, 'drivers'));
      if (!driversSnapshot.exists()) {
        message.destroy();
        message.warning('Brak kierowców w bazie');
        setLoading(false);
        return;
      }

      const driversData = driversSnapshot.val();
      const reportData = [];

      // Для каждого водителя собираем данные по umowa_najmu за все недели
      for (const [driverId, driverData] of Object.entries(driversData)) {
        const akronim = driverData.akronim || 'N/A';
        const driverName = driverData.name || driverId;
        let totalUmowaNajmu = 0;
        const weeklyData = {};

        // Проходим по всем неделям
        for (const week of weeks) {
          const weekPath = `drivers/${driverId}/weeks/${week.weekKey}/summary`;
          const weekSnapshot = await get(ref(db, weekPath));

          if (weekSnapshot.exists()) {
            const summary = weekSnapshot.val();
            let umowaNajmu = parseFloat(summary.umowa_najmu || 0);
            // Заменяем отрицательные значения на 0
            if (umowaNajmu < 0) umowaNajmu = 0;
            totalUmowaNajmu += umowaNajmu;
            weeklyData[week.weekKey] = umowaNajmu;
          } else {
            weeklyData[week.weekKey] = 0;
          }
        }

        // Добавляем в отчёт только если есть данные
        if (totalUmowaNajmu > 0 || Object.keys(weeklyData).length > 0) {
          reportData.push({
            driverId,
            akronim,
            driverName,
            ...weeklyData,
            total: totalUmowaNajmu,
          });
        }
      }

      message.destroy();

      if (reportData.length === 0) {
        message.info('Brak danych dla wybranego okresu');
        setLoading(false);
        return;
      }

      // Сохраняем отчёт в Firebase
      const reportRef = push(ref(db, 'reports'));
      const reportRecord = {
        type: 'umowa_najmu',
        format: selectedFormat,
        title: `Raport Umowa Najmu - ${startDate.format('MMMM YYYY')}`,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        createdAt: Date.now(),
        weeksCount: weeks.length,
        driversCount: reportData.length,
        data: reportData,
      };

      await set(reportRef, reportRecord);

      message.success(`Raport wygenerowany! Kierowców: ${reportData.length}, Tygodni: ${weeks.length}`);

      // Обновляем историю
      await loadReportsHistory();

      // Экспортируем в зависимости от формата
      if (selectedFormat === 'csv') {
        exportUmowaNajmuToCSV(reportRecord);
      } else {
        exportToExcel(reportRecord);
      }

    } catch (error) {
      console.error('Error generating report:', error);
      message.destroy();
      message.error('Błąd podczas generowania raportu');
    } finally {
      setLoading(false);
    }
  };

  // Универсальная функция генерации отчёта
  const generateReport = async () => {
    if (selectedReportType === 'gotowka') {
      await generateGotowkaReport();
    } else if (selectedReportType === 'umowa_najmu') {
      await generateUmowaNajmuReport();
    }
  };

  // Генерация отчёта Gotówka (CSV)
  const generateGotowkaReport = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error('Wybierz okres dla raportu');
      return;
    }

    setLoading(true);
    const startDate = dateRange[0];
    const endDate = dateRange[1];

    try {
      message.loading('Generowanie raportu gotówka...', 0);

      // Получаем все недели в выбранном месяце
      const weeks = getWeeksInMonth(startDate, endDate);

      if (weeks.length === 0) {
        message.destroy();
        message.warning('Brak tygodni w wybranym okresie');
        setLoading(false);
        return;
      }

      // Загружаем всех водителей
      const driversSnapshot = await get(ref(db, 'drivers'));
      if (!driversSnapshot.exists()) {
        message.destroy();
        message.warning('Brak kierowców w bazie');
        setLoading(false);
        return;
      }

      const driversData = driversSnapshot.val();
      const reportData = [];

      // Для каждого водителя собираем данные по gotówka и umowa_najmu за все недели
      for (const [driverId, driverData] of Object.entries(driversData)) {
        const akronim = String(driverData.akronim || '00000');
        const driverName = driverData.name || driverId;
        let totalNajem = 0;
        let totalPobrana = 0;

        // Проходим по всем неделям
        for (const week of weeks) {
          const weekPath = `drivers/${driverId}/weeks/${week.weekKey}/summary`;
          const weekSnapshot = await get(ref(db, weekPath));

          if (weekSnapshot.exists()) {
            const summary = weekSnapshot.val();
            const umowaNajmu = parseFloat(summary.umowa_najmu || 0);
            const gotowka = parseFloat(summary.gotowka || 0);
            totalNajem += umowaNajmu;
            totalPobrana += Math.abs(gotowka); // Убираем минус
          }
        }

        // Заменяем отрицательные najem на 0
        if (totalNajem < 0) totalNajem = 0;

        // Добавляем в отчёт только если есть данные
        if (totalNajem > 0 || totalPobrana > 0) {
          reportData.push({
            akronim,
            driverName,
            najem: totalNajem,
            pobrana: totalPobrana,
          });
        }
      }

      message.destroy();

      if (reportData.length === 0) {
        message.info('Brak danych dla wybranego okresu');
        setLoading(false);
        return;
      }

      // Сохраняем отчёт в Firebase
      const reportRef = push(ref(db, 'reports'));
      const reportRecord = {
        type: 'gotowka',
        format: selectedFormat,
        title: `Raport Gotówka - ${startDate.format('MMMM YYYY')}`,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        createdAt: Date.now(),
        weeksCount: weeks.length,
        driversCount: reportData.length,
        data: reportData,
      };

      await set(reportRef, reportRecord);

      message.success(`Raport wygenerowany! Kierowców: ${reportData.length}`);

      // Обновляем историю
      await loadReportsHistory();

      // Экспортируем в зависимости от формата
      if (selectedFormat === 'csv') {
        exportToCSV(reportRecord);
      } else {
        exportGotowkaToExcel(reportRecord);
      }

    } catch (error) {
      console.error('Error generating gotowka report:', error);
      message.destroy();
      message.error('Błąd podczas generowania raportu');
    } finally {
      setLoading(false);
    }
  };

  // Экспорт в CSV
  const exportToCSV = (report) => {
    try {
      const data = report.data || [];

      // Создаём CSV контент с BOM для правильного отображения в Excel
      let csvContent = '\uFEFF'; // BOM для UTF-8

      // Добавляем заголовок с датами и номером недели
      if (report.startDate && report.endDate) {
        const startWeek = dayjs(report.startDate).isoWeek();
        const endWeek = dayjs(report.endDate).isoWeek();
        const weekRange = startWeek === endWeek ? `${startWeek} tydzień` : `${startWeek}-${endWeek} tydzień`;
        csvContent += `Raport Gotówka\n`;
        csvContent += `Zakres dat: ${dayjs(report.startDate).format('DD.MM.YYYY')} - ${dayjs(report.endDate).format('DD.MM.YYYY')}\n`;
        csvContent += `${weekRange}\n`;
        csvContent += '\n';
      }

      csvContent += 'AKRONIM;IMIĘ NAZWISKO;NAJEM;POBRANA\n';

      data.forEach((row) => {
        const akronim = String(row.akronim || '00000');
        const driverName = row.driverName || 'N/A';
        let najem = parseFloat(row.najem || 0);
        // Заменяем отрицательные najem на 0
        if (najem < 0) najem = 0;
        const pobrana = parseFloat(row.pobrana || 0).toFixed(2);
        // Добавляем = для сохранения ведущих нулей в Excel
        csvContent += `="${akronim}";${driverName};${najem.toFixed(2)};${pobrana}\n`;
      });

      // Создаём Blob и скачиваем
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Формируем название файла с номером недели и датами
      let fileName = 'Gotowka';
      if (report.startDate && report.endDate) {
        const startWeek = dayjs(report.startDate).isoWeek();
        const endWeek = dayjs(report.endDate).isoWeek();
        const weekRange = startWeek === endWeek ? `${startWeek}` : `${startWeek}-${endWeek}`;
        const dateRange = `${dayjs(report.startDate).format('DD.MM')}-${dayjs(report.endDate).format('DD.MM.YYYY')}`;
        fileName = `Gotowka_tydzien_${weekRange}_${dateRange}.csv`;
      } else {
        fileName = `${report.title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success('Plik CSV został pobrany');
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      message.error('Błąd podczas eksportu do CSV');
    }
  };

  // Экспорт Gotówka в Excel
  const exportGotowkaToExcel = (report) => {
    try {
      const data = report.data || [];

      // Подготовка данных для Excel
      const excelData = data.map((row) => {
        let najem = parseFloat(row.najem || 0);
        // Заменяем отрицательные najem на 0
        if (najem < 0) najem = 0;

        return {
          'AKRONIM': String(row.akronim), // Явно преобразуем в строку
          'IMIĘ NAZWISKO': row.driverName || 'N/A',
          'NAJEM': najem.toFixed(2),
          'POBRANA': parseFloat(row.pobrana || 0).toFixed(2),
        };
      });

      // Создание workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Gotówka');

      // Устанавливаем формат текста для колонки AKRONIM
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 }); // Колонка A (AKRONIM)
        if (ws[cellAddress]) {
          ws[cellAddress].t = 's'; // Устанавливаем тип "строка"
          ws[cellAddress].z = '@'; // Формат текста
        }
      }

      // Автоширина колонок
      const maxWidth = 20;
      const wscols = Object.keys(excelData[0] || {}).map(() => ({ wch: maxWidth }));
      ws['!cols'] = wscols;

      // Скачивание файла
      // Формируем название файла с номером недели и датами
      let fileName = 'Gotowka';
      if (report.startDate && report.endDate) {
        const startWeek = dayjs(report.startDate).isoWeek();
        const endWeek = dayjs(report.endDate).isoWeek();
        const weekRange = startWeek === endWeek ? `${startWeek}` : `${startWeek}-${endWeek}`;
        const dateRange = `${dayjs(report.startDate).format('DD.MM')}-${dayjs(report.endDate).format('DD.MM.YYYY')}`;
        fileName = `Gotowka_tydzien_${weekRange}_${dateRange}.xlsx`;
      } else {
        fileName = `${report.title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      }
      XLSX.writeFile(wb, fileName);

      message.success('Plik Excel został pobrany');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error('Błąd podczas eksportu do Excel');
    }
  };

  // Экспорт Umowa Najmu в CSV
  const exportUmowaNajmuToCSV = (report) => {
    try {
      const data = report.data || [];

      if (data.length === 0) {
        message.warning('Brak danych do eksportu');
        return;
      }

      // Создаём CSV контент с BOM для правильного отображения в Excel
      let csvContent = '\uFEFF'; // BOM для UTF-8

      // Добавляем заголовок с датами и номером недели
      if (report.startDate && report.endDate) {
        const startWeek = dayjs(report.startDate).isoWeek();
        const endWeek = dayjs(report.endDate).isoWeek();
        const weekRange = startWeek === endWeek ? `${startWeek} tydzień` : `${startWeek}-${endWeek} tydzień`;
        csvContent += `Raport Umowa Najmu\n`;
        csvContent += `Zakres dat: ${dayjs(report.startDate).format('DD.MM.YYYY')} - ${dayjs(report.endDate).format('DD.MM.YYYY')}\n`;
        csvContent += `${weekRange}\n`;
        csvContent += '\n';
      }

      // Создаём заголовки CSV
      const firstRow = data[0];
      const weekKeys = Object.keys(firstRow).filter(
        (key) => key !== 'driverId' && key !== 'akronim' && key !== 'driverName' && key !== 'total'
      );

      csvContent += 'Akronim;Imię Nazwisko;' + weekKeys.join(';') + ';RAZEM\n';

      // Добавляем строки данных
      data.forEach((row) => {
        const akronim = String(row.akronim || 'N/A');
        const driverName = row.driverName || row.driverId || 'N/A';
        const weekValues = weekKeys.map((key) => {
          let value = parseFloat(row[key] || 0);
          // Заменяем отрицательные значения на 0
          if (value < 0) value = 0;
          return value.toFixed(2);
        });
        let total = parseFloat(row.total || 0);
        // Заменяем отрицательный total на 0
        if (total < 0) total = 0;
        csvContent += `="${akronim}";${driverName};${weekValues.join(';')};${total.toFixed(2)}\n`;
      });

      // Создаём Blob и скачиваем
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Формируем название файла с номером недели и датами
      let fileName = 'Umowa_Najmu';
      if (report.startDate && report.endDate) {
        const startWeek = dayjs(report.startDate).isoWeek();
        const endWeek = dayjs(report.endDate).isoWeek();
        const weekRange = startWeek === endWeek ? `${startWeek}` : `${startWeek}-${endWeek}`;
        const dateRange = `${dayjs(report.startDate).format('DD.MM')}-${dayjs(report.endDate).format('DD.MM.YYYY')}`;
        fileName = `Umowa_Najmu_tydzien_${weekRange}_${dateRange}.csv`;
      } else {
        fileName = `${report.title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success('Plik CSV został pobrany');
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      message.error('Błąd podczas eksportu do CSV');
    }
  };

  // Экспорт Umowa Najmu в Excel
  const exportToExcel = (report) => {
    try {
      const data = report.data || [];

      // Подготовка данных для Excel
      const excelData = data.map((row) => {
        const { driverId, akronim, driverName, total, ...weeks } = row;
        // Заменяем отрицательные значения в weeks на 0
        const cleanWeeks = {};
        Object.keys(weeks).forEach((key) => {
          let value = parseFloat(weeks[key] || 0);
          if (value < 0) value = 0;
          cleanWeeks[key] = value.toFixed(2);
        });
        // Заменяем отрицательный total на 0
        let cleanTotal = parseFloat(total || 0);
        if (cleanTotal < 0) cleanTotal = 0;

        return {
          'Akronim': akronim,
          'Imię Nazwisko': driverName || driverId || 'N/A',
          ...cleanWeeks,
          'RAZEM': cleanTotal.toFixed(2),
        };
      });

      // Создание workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Umowa Najmu');

      // Автоширина колонок
      const maxWidth = 20;
      const wscols = Object.keys(excelData[0] || {}).map(() => ({ wch: maxWidth }));
      ws['!cols'] = wscols;

      // Скачивание файла
      // Формируем название файла с номером недели и датами
      let fileName = 'Umowa_Najmu';
      if (report.startDate && report.endDate) {
        const startWeek = dayjs(report.startDate).isoWeek();
        const endWeek = dayjs(report.endDate).isoWeek();
        const weekRange = startWeek === endWeek ? `${startWeek}` : `${startWeek}-${endWeek}`;
        const dateRange = `${dayjs(report.startDate).format('DD.MM')}-${dayjs(report.endDate).format('DD.MM.YYYY')}`;
        fileName = `Umowa_Najmu_tydzien_${weekRange}_${dateRange}.xlsx`;
      } else {
        fileName = `${report.title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      }
      XLSX.writeFile(wb, fileName);

      message.success('Plik Excel został pobrany');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error('Błąd podczas eksportu do Excel');
    }
  };

  // Колонки для таблицы истории отчётов
  const historyColumns = [
    {
      title: 'Nazwa raportu',
      dataIndex: 'title',
      key: 'title',
      render: (text) => (
        <Space>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Okres',
      key: 'period',
      width: 200,
      render: (_, record) => (
        <Text>
          {dayjs(record.startDate).format('DD.MM.YYYY')} - {dayjs(record.endDate).format('DD.MM.YYYY')}
        </Text>
      ),
    },
    {
      title: 'Kierowców',
      dataIndex: 'driversCount',
      key: 'driversCount',
      width: 100,
      align: 'center',
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Tygodni',
      dataIndex: 'weeksCount',
      key: 'weeksCount',
      width: 100,
      align: 'center',
      render: (count) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: 80,
      align: 'center',
      render: (format) => {
        const fmt = format || 'csv';
        return (
          <Tag color={fmt === 'csv' ? 'green' : 'blue'}>
            {fmt.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Data utworzenia',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (timestamp) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
          <Text type="secondary">{dayjs(timestamp).format('DD.MM.YYYY HH:mm')}</Text>
        </Space>
      ),
    },
    {
      title: 'Akcje',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => {
        const format = record.format || 'csv'; // По умолчанию CSV для старых записей

        const handleExport = () => {
          if (record.type === 'gotowka') {
            if (format === 'csv') {
              exportToCSV(record);
            } else {
              exportGotowkaToExcel(record);
            }
          } else if (record.type === 'umowa_najmu') {
            if (format === 'csv') {
              exportUmowaNajmuToCSV(record);
            } else {
              exportToExcel(record);
            }
          }
        };

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={format === 'csv' ? <FileDoneOutlined /> : <FileExcelOutlined />}
              onClick={handleExport}
              style={{
                background: format === 'csv' ? '#52c41a' : '#1890ff',
                borderColor: format === 'csv' ? '#52c41a' : '#1890ff',
              }}
            >
              {format.toUpperCase()}
            </Button>
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => {
                setPreviewData(record.data || []);
                setPreviewVisible(true);
              }}
            >
              Podgląd
            </Button>
          </Space>
        );
      },
    },
  ];

  // Колонки для предпросмотра данных
  const getPreviewColumns = () => {
    if (previewData.length === 0) return [];

    const firstRow = previewData[0];

    // Если это отчёт gotowka (CSV)
    if (firstRow.najem !== undefined || firstRow.pobrana !== undefined) {
      return [
        {
          title: 'AKRONIM',
          dataIndex: 'akronim',
          key: 'akronim',
          width: 150,
          render: (akronim) => <Tag color="purple">{String(akronim)}</Tag>,
        },
        {
          title: 'NAJEM',
          dataIndex: 'najem',
          key: 'najem',
          width: 150,
          align: 'right',
          render: (value) => (
            <Text strong style={{ fontFamily: 'monospace', color: '#1890ff' }}>
              {parseFloat(value || 0).toFixed(2)} PLN
            </Text>
          ),
        },
        {
          title: 'POBRANA',
          dataIndex: 'pobrana',
          key: 'pobrana',
          width: 150,
          align: 'right',
          render: (value) => (
            <Text strong style={{ fontFamily: 'monospace', color: '#52c41a' }}>
              {parseFloat(value || 0).toFixed(2)} PLN
            </Text>
          ),
        },
      ];
    }

    // Отчёт umowa_najmu (Excel)
    const baseColumns = [
      {
        title: 'Driver ID',
        dataIndex: 'driverId',
        key: 'driverId',
        fixed: 'left',
        width: 130,
        render: (id) => <Text strong style={{ color: '#1890ff' }}>{id}</Text>,
      },
      {
        title: 'Akronim',
        dataIndex: 'akronim',
        key: 'akronim',
        width: 100,
        render: (akronim) => <Tag color="purple">{akronim}</Tag>,
      },
    ];

    // Динамические колонки для недель
    const weekColumns = Object.keys(firstRow)
      .filter((key) => key !== 'driverId' && key !== 'akronim' && key !== 'total')
      .map((weekKey) => ({
        title: weekKey,
        dataIndex: weekKey,
        key: weekKey,
        width: 120,
        align: 'right',
        render: (value) => (
          <Text style={{ fontFamily: 'monospace' }}>
            {parseFloat(value || 0).toFixed(2)} PLN
          </Text>
        ),
      }));

    const totalColumn = {
      title: 'RAZEM',
      dataIndex: 'total',
      key: 'total',
      fixed: 'right',
      width: 150,
      align: 'right',
      render: (value) => (
        <Text strong style={{ color: '#52c41a', fontSize: '15px' }}>
          {parseFloat(value || 0).toFixed(2)} PLN
        </Text>
      ),
    };

    return [...baseColumns, ...weekColumns, totalColumn];
  };

  return (
    <div style={{
      height: 'calc(100vh - 112px)',
      display: 'flex',
      flexDirection: 'column',
      padding: '24px'
    }}>
      {/* Генерация отчёта */}
      <div style={{ marginBottom: '24px', flexShrink: 0 }}>
        <Row gutter={16} align="bottom">
          <Col flex="0 0 200px">
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text strong>Typ raportu:</Text>
              <Select
                value={selectedReportType}
                onChange={setSelectedReportType}
                style={{ width: '100%' }}
                size="large"
              >
                <Option value="gotowka">Gotówka</Option>
              </Select>
            </Space>
          </Col>

          <Col flex="0 0 150px">
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text strong>Format:</Text>
              <Select
                value={selectedFormat}
                onChange={setSelectedFormat}
                style={{ width: '100%' }}
                size="large"
              >
                <Option value="csv">CSV</Option>
                <Option value="excel">Excel</Option>
              </Select>
            </Space>
          </Col>

          <Col flex="0 0 300px">
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text strong>Okres:</Text>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                picker="week"
                placeholder={['Od', 'Do']}
                size="large"
              />
            </Space>
          </Col>

          <Col flex="1">
            <Button
              type="primary"
              size="large"
              icon={selectedFormat === 'csv' ? <FileDoneOutlined /> : <FileExcelOutlined />}
              onClick={generateReport}
              loading={loading}
              block
              style={{
                height: '40px',
                background: selectedFormat === 'csv' ? '#52c41a' : '#1890ff',
                borderColor: selectedFormat === 'csv' ? '#52c41a' : '#1890ff',
              }}
            >
              Generuj raport {selectedFormat.toUpperCase()}
            </Button>
          </Col>
        </Row>
      </div>

      {/* История отчётов */}
      <Card
        bordered={true}
        style={{
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        bodyStyle={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '24px',
        }}
      >
        <Title level={4} style={{ marginBottom: '16px', flexShrink: 0 }}>
          <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          Historia raportów ({generatedReports.length})
        </Title>

        <Table
          columns={historyColumns}
          dataSource={generatedReports}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Łącznie: ${total} raportów`,
            position: ['bottomCenter'],
          }}
          scroll={{ x: 1000 }}
          style={{ flex: 1 }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div style={{ color: '#8c8c8c' }}>Brak wygenerowanych raportów</div>
              </div>
            ),
          }}
        />
      </Card>

      {/* Модальное окно предпросмотра */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>Podgląd raportu</span>
          </Space>
        }
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <Table
          columns={getPreviewColumns()}
          dataSource={previewData}
          rowKey={(record) => record.driverId || record.akronim}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default Raporty;
